import { apiRequest } from "./api";
import type {
  AdultGigRequestListItemResponse,
  AdultGigRequestTeenDetailResponse,
  CreateGigFundingSessionRequest,
  CreateGigFundingSessionResponse,
  GigFundingStatusResponse,
  CreateGigRequest,
    CreateGigResponse,
    GigDetailResponse,
    GigListItemResponse,
    GigListRequest,
    GigListResponse,
    GigPosterProfileResponse,
    GigRequestRequest,
  LandingGigResponse,
  GigRequestResponse,
  ProceedWithSelectedCrewResponse,
  PublicGigPreviewResponse,
  RecentlyCompletedGigsResponse,
  ScheduleProposalRequest,
  PosterDecisionRequest,
  PosterDecisionResponse,
  TeenProfileResponse,
  TeenGigRequestListItemResponse,
  UpdateTeenProfileRequest,
} from "../types/gigTypes";

export const gigsApi = {
  create(payload: CreateGigRequest): Promise<CreateGigResponse> {
    return apiRequest<CreateGigResponse>("/api/gigs/create", {
      method: "POST",
      body: payload,
    });
  },

  updateEditableGig(gigId: string, payload: CreateGigRequest): Promise<CreateGigResponse> {
    return apiRequest<CreateGigResponse>(`/api/adult/gigs/${gigId}`, {
      method: "POST",
      body: payload,
    });
  },

  list(payload: GigListRequest): Promise<GigListResponse> {
    return apiRequest<GigListResponse>("/api/gigs", {
      method: "POST",
      body: payload,
    });
  },

  requestAsTeen(gigId: string, payload: GigRequestRequest = {}): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gigs/${gigId}/requests`, {
      method: "POST",
      body: payload,
    });
  },

  listTeenRequestedGigs(): Promise<TeenGigRequestListItemResponse[]> {
    return apiRequest<TeenGigRequestListItemResponse[]>("/api/teen/gigs/requests", {
      method: "GET",
    });
  },

  withdrawTeenGigRequest(requestId: string): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gig-requests/${requestId}/withdraw`, {
      method: "POST",
    });
  },

  submitTeenScheduleProposal(requestId: string, payload: ScheduleProposalRequest): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gig-requests/${requestId}/schedule-proposals`, {
      method: "POST",
      body: payload,
    });
  },

  acknowledgeTeenGigChange(requestId: string): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gig-requests/${requestId}/acknowledge-gig-change`, {
      method: "POST",
    });
  },

  confirmTeenSuggestedTime(requestId: string): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gig-requests/${requestId}/confirm-suggested-time`, {
      method: "POST",
    });
  },

  unconfirmTeenSuggestedTime(requestId: string): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/teen/gig-requests/${requestId}/unconfirm-suggested-time`, {
      method: "POST",
    });
  },

  listAdultPostedGigs(): Promise<GigListItemResponse[]> {
    return apiRequest<GigListItemResponse[]>("/api/adult/gigs", {
      method: "GET",
    });
  },

  repostGig(gigId: string, payload: CreateGigRequest): Promise<CreateGigResponse> {
    return apiRequest<CreateGigResponse>(`/api/adult/gigs/${gigId}/repost`, {
      method: "POST",
      body: payload,
    });
  },

  cancelGig(gigId: string): Promise<GigDetailResponse> {
    return apiRequest<GigDetailResponse>(`/api/adult/gigs/${gigId}/cancel`, {
      method: "POST",
    });
  },

  getById(gigId: string): Promise<GigDetailResponse> {
    return apiRequest<GigDetailResponse>(`/api/gigs/${gigId}`, {
      method: "GET",
    });
  },

  getPublicPreview(gigId: string): Promise<PublicGigPreviewResponse> {
    return apiRequest<PublicGigPreviewResponse>(`/api/public/gigs/${gigId}/preview`, {
      method: "GET",
    });
  },

  getLandingFeaturedGig(): Promise<LandingGigResponse | undefined> {
    return apiRequest<LandingGigResponse | undefined>("/api/public/landing/featured-gig", {
      method: "GET",
    });
  },

  listLandingFeaturedGigs(): Promise<LandingGigResponse[]> {
    return apiRequest<LandingGigResponse[]>("/api/public/landing/featured-gigs", {
      method: "GET",
    });
  },

  listRecentlyCompleted(): Promise<RecentlyCompletedGigsResponse> {
    return apiRequest<RecentlyCompletedGigsResponse>("/api/gigs/recently-completed", {
      method: "GET",
    });
  },

  getPosterProfile(gigId: string): Promise<GigPosterProfileResponse> {
    return apiRequest<GigPosterProfileResponse>(`/api/gigs/${gigId}/poster-profile`, {
      method: "GET",
    });
  },

  listAdultRequestsForGig(gigId: string): Promise<AdultGigRequestListItemResponse[]> {
    return apiRequest<AdultGigRequestListItemResponse[]>(`/api/adult/gigs/${gigId}/requests`, {
      method: "GET",
    });
  },

  getAdultTeenDetailForGigRequest(gigId: string, requestId: string): Promise<AdultGigRequestTeenDetailResponse> {
    return apiRequest<AdultGigRequestTeenDetailResponse>(`/api/adult/gigs/${gigId}/requests/${requestId}/teen`, {
      method: "GET",
    });
  },

  createPosterDecision(requestId: string, payload: PosterDecisionRequest): Promise<PosterDecisionResponse> {
    return apiRequest<PosterDecisionResponse>(`/api/adult/gig-requests/${requestId}/poster-decisions`, {
      method: "POST",
      body: payload,
    });
  },

  proceedWithSelectedCrew(gigId: string): Promise<ProceedWithSelectedCrewResponse> {
    return apiRequest<ProceedWithSelectedCrewResponse>(`/api/adult/gigs/${gigId}/proceed-with-selected-crew`, {
      method: "POST",
    });
  },

  createFundingCheckoutSession(requestId: string, payload: CreateGigFundingSessionRequest): Promise<CreateGigFundingSessionResponse> {
    return apiRequest<CreateGigFundingSessionResponse>(`/api/adult/gig-requests/${requestId}/funding-checkout-session`, {
      method: "POST",
      body: payload,
    });
  },

  createCombinedFundingCheckoutSession(gigId: string, payload: CreateGigFundingSessionRequest): Promise<CreateGigFundingSessionResponse> {
    return apiRequest<CreateGigFundingSessionResponse>(`/api/adult/gigs/${gigId}/combined-funding-checkout-session`, {
      method: "POST",
      body: payload,
    });
  },

  getFundingStatus(paymentId: string): Promise<GigFundingStatusResponse> {
    return apiRequest<GigFundingStatusResponse>(`/api/adult/gig-request-payments/${paymentId}`, {
      method: "GET",
    });
  },

  submitAdultScheduleProposal(requestId: string, payload: ScheduleProposalRequest): Promise<GigRequestResponse> {
    return apiRequest<GigRequestResponse>(`/api/adult/gig-requests/${requestId}/schedule-proposals`, {
      method: "POST",
      body: payload,
    });
  },

  getTeenProfile(): Promise<TeenProfileResponse> {
    return apiRequest<TeenProfileResponse>("/api/teen/profile", {
      method: "GET",
    });
  },

  updateTeenProfile(payload: UpdateTeenProfileRequest): Promise<TeenProfileResponse> {
    return apiRequest<TeenProfileResponse>("/api/teen/profile", {
      method: "POST",
      body: payload,
    });
  },
};
